import { Button } from "@/components/ui/Button";
import { cn } from "@/utils/cn";
import {
  CalendarClock,
  Check,
  ChevronRight,
  Columns3,
  Eraser,
  FileText,
  Folder,
  History,
  Pencil,
  RefreshCw,
  Table2,
  Trash2,
  X,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useId, useMemo, useRef, useState } from "react";
import { useChatSession } from "../context/ChatSessionProvider";
import type { TraceTurn } from "../types";
import { noteSummary, parseDatasetNote } from "../utils/datasetNote";

interface Attachment {
  filename: string;
  size?: number;
  kind?: "file" | "folder";
}

interface ScheduledRun {
  monitor: string;
  trigger: string;
  schedule: string;
}

interface CollectionTurn {
  turn: number;
  max: number;
}

interface ParsedUserContent {
  text: string;
  attachments: Attachment[];
  scheduled: ScheduledRun | null;
  /** A turn that Paperclip started to continue a dataset's collection. */
  collectionTurn: CollectionTurn | null;
  /** The kind of a note that the Datasets page saved for the user, such as cleared_cells. */
  note: string | null;
}

const unescapeAttribute = (value: string) =>
  value
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");

/** A dataset monitor's run starts with a `<scheduled_run … />` tag; its saved prompt follows. */
const parseScheduledRun = (raw: string): { run: ScheduledRun | null; rest: string } => {
  const match = raw.match(/^\s*<scheduled_run\s+([^>]*?)\s*\/>/);
  if (!match) return { run: null, rest: raw };
  const attributes = new Map<string, string>();
  for (const [, key, value] of match[1].matchAll(/(\w+)="([^"]*)"/g)) {
    attributes.set(key, unescapeAttribute(value));
  }
  return {
    run: {
      monitor: attributes.get("monitor") ?? "Monitor",
      trigger: attributes.get("trigger") ?? "schedule",
      schedule: attributes.get("schedule") ?? "",
    },
    rest: raw.slice(match[0].length),
  };
};

/** A turn that continues a dataset's collection starts with a `<collection_turn … />` tag. */
const parseCollectionTurn = (raw: string): { turn: CollectionTurn | null; rest: string } => {
  const match = raw.match(/^\s*<collection_turn\s+turn="(\d+)"\s+max="(\d+)"\s*\/>/);
  if (!match) return { turn: null, rest: raw };
  return {
    turn: { turn: Number(match[1]), max: Number(match[2]) },
    rest: raw.slice(match[0].length),
  };
};

/** The kinds of note that the server writes for a change on the Datasets page. */
const DATASET_NOTES: Record<string, { label: string; icon: LucideIcon }> = {
  cleared_cells: { label: "Cells cleared in the grid", icon: Eraser },
  deleted_rows: { label: "Rows deleted from the grid", icon: Trash2 },
  grid_changed: { label: "Grid changed", icon: Eraser },
  columns_changed: { label: "Proposed columns changed", icon: Columns3 },
  columns_approved: { label: "Columns approved, collection not started", icon: Check },
  column_added: { label: "Column added", icon: Columns3 },
  column_changed: { label: "Column changed", icon: Columns3 },
  column_deleted: { label: "Column deleted", icon: Columns3 },
  checkpoint_saved: { label: "Checkpoint saved", icon: History },
  checkpoint_restored: { label: "Checkpoint restored", icon: History },
  monitor_paused: { label: "Monitor paused", icon: CalendarClock },
  monitor_resumed: { label: "Monitor resumed", icon: CalendarClock },
  monitor_deleted: { label: "Monitor deleted", icon: CalendarClock },
};

const datasetNote = (kind: string) =>
  DATASET_NOTES[kind] ?? { label: "Dataset change", icon: Table2 };

/**
 * A note is not a message that waits for a reply, so it is a faded line on the
 * left, not a slip. Its first line shows; the lists after it open on request.
 */
const DatasetNoteLine = ({ kind, text }: { kind: string; text: string }) => {
  const [open, setOpen] = useState(false);
  const detailsId = useId();
  const { label, icon: Icon } = datasetNote(kind);
  const [first = "", ...rest] = text.split("\n");
  const summary = noteSummary(first.trim());
  const details = rest.join("\n").trim();
  return (
    <div
      className="chat-note flex flex-col items-start gap-0.5 text-left text-xs leading-5 text-zinc-500"
      data-dataset-note={kind}
    >
      <p className="flex items-center gap-1.5 font-medium">
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>Note</span>
        <span aria-hidden>·</span>
        <span>{label}</span>
      </p>
      {summary && <p className="whitespace-pre-wrap pl-5">{summary}</p>}
      {details && (
        <>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={detailsId}
            onClick={() => setOpen((current) => !current)}
            className="ml-5 inline-flex items-center gap-1 rounded-[3px] text-zinc-400 hover:text-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900"
          >
            <ChevronRight
              className={cn("h-3 w-3 motion-safe:transition-transform", open && "rotate-90")}
              aria-hidden
            />
            {open ? "Hide details" : "Show details"}
          </button>
          {open && (
            <p id={detailsId} className="whitespace-pre-wrap pl-5">
              {details}
            </p>
          )}
        </>
      )}
    </div>
  );
};

function parseUserContent(raw: string | undefined): ParsedUserContent {
  if (!raw) {
    return { text: "", attachments: [], scheduled: null, collectionTurn: null, note: null };
  }

  const { run: scheduled, rest: afterRun } = parseScheduledRun(raw);
  const { turn: collectionTurn, rest: afterTurn } = parseCollectionTurn(afterRun);
  const { note, rest } = parseDatasetNote(afterTurn);
  const attachments: Attachment[] = [];
  let cleaned = rest.replace(
    /<uploaded_document\s+[^>]*filename="([^"]+)"[^>]*>\s*<\/uploaded_document>/g,
    (match, filename) => {
      const sizeMatch = match.match(/size="(\d+)"/);
      attachments.push({ filename, size: sizeMatch ? parseInt(sizeMatch[1], 10) : undefined, kind: "file" });
      return "";
    },
  );
  // Whole-folder attachments from the Clipboard "Start chat" action.
  cleaned = cleaned.replace(
    /<clipboard_folder\s+[^>]*name="([^"]+)"[^>]*>\s*<\/clipboard_folder>/g,
    (_match, name) => {
      attachments.push({ filename: name, kind: "folder" });
      return "";
    },
  );

  return { text: cleaned.trim(), attachments, scheduled, collectionTurn, note };
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
  turn: TraceTurn;
  /** Side-panel layout: keep the slip inside the thread's padding. */
  compact?: boolean;
}

export const UserMessage = ({ turn, compact = false }: Props) => {
  const { editMessage } = useChatSession();
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editText, setEditText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const canEdit = !turn.id.startsWith("pending-");

  const { text, attachments, scheduled, collectionTurn, note } = useMemo(
    () => parseUserContent(turn.content),
    [turn.content],
  );
  const automatic = Boolean(scheduled || collectionTurn);

  const startEdit = useCallback(() => {
    setEditText(turn.content ?? "");
    setIsEditing(true);
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.select();
    });
  }, [turn.content]);

  const submitEdit = useCallback(async () => {
    const trimmed = editText.trim();
    if (!trimmed || trimmed === (turn.content ?? "").trim()) {
      setIsEditing(false);
      return;
    }
    setIsSubmitting(true);
    try {
      await editMessage(turn.id, trimmed);
      setIsEditing(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [editText, turn.content, turn.id, editMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        submitEdit();
      }
      if (e.key === "Escape") {
        setIsEditing(false);
      }
    },
    [submitEdit],
  );

  if (note) return <DatasetNoteLine kind={note} text={text} />;

  if (isEditing) {
    return (
      <div
        className={cn(
          "flex justify-end bg-zinc-900/[0.05] py-4",
          compact ? "px-4" : "-mr-5 px-5",
        )}
      >
        <div className="w-full max-w-[75%] space-y-2">
          <textarea
            ref={textareaRef}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full resize-none rounded-[3px] border border-zinc-300 bg-white px-3 py-2 text-base leading-6 text-zinc-900 outline-none focus:border-zinc-400 focus:ring-1 focus:ring-zinc-400"
            rows={3}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(false)}
              disabled={isSubmitting}
              className="text-xs text-zinc-500"
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => void submitEdit()}
              loading={isSubmitting}
              disabled={
                !editText.trim() ||
                editText.trim() === (turn.content ?? "").trim()
              }
              className="text-xs"
            >
              <Check className="h-3.5 w-3.5" />
              Save & resend
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    // "Paper insert": a tinted slip laid on the page, square corners, no label,
    // set to the right so the user's turns read against the assistant's column.
    <div className="chat-user group flex items-start justify-end gap-2">
      {canEdit && text && !automatic && (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={startEdit}
          aria-label="Edit message"
          title="Edit message"
          className={cn(
            "mt-3.5 h-6 w-6 shrink-0 rounded text-zinc-400 opacity-0 transition-opacity hover:text-zinc-700",
            "group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100",
          )}
        >
          <Pencil className="h-3 w-3" aria-hidden />
        </Button>
      )}
      <div
        className={cn(
          "flex w-fit max-w-[75%] flex-col items-end gap-2.5 bg-zinc-900/[0.05] px-4 py-3.5 rounded-sm",
          !compact && "-mr-4",
          automatic && "border border-dashed border-zinc-900/20",
        )}
      >
        {collectionTurn && (
          <p className="flex flex-wrap items-center justify-end gap-x-1.5 text-xs font-medium text-zinc-500">
            <RefreshCw className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>Collection continues</span>
            <span aria-hidden>·</span>
            <span className="text-zinc-700">
              Turn {collectionTurn.turn} of up to {collectionTurn.max}
            </span>
          </p>
        )}
        {scheduled && (
          <p className="flex flex-wrap items-center justify-end gap-x-1.5 text-xs font-medium text-zinc-500">
            <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>{scheduled.trigger === "manual" ? "Monitor run" : "Scheduled run"}</span>
            <span aria-hidden>·</span>
            <span className="text-zinc-700">{scheduled.monitor}</span>
            {scheduled.schedule && (
              <>
                <span aria-hidden>·</span>
                <span>{scheduled.schedule}</span>
              </>
            )}
          </p>
        )}
        {attachments.length > 0 && (
          <div className="flex flex-wrap justify-end gap-1.5">
            {attachments.map((att) => {
              const Icon = att.kind === "folder" ? Folder : FileText;
              return (
                <div
                  key={`${att.kind}-${att.filename}`}
                  className="flex items-center gap-1.5 rounded-sm border border-zinc-900/[0.12] bg-white/60 px-2.5 py-1 text-sm text-zinc-600"
                >
                  <Icon className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
                  <span className="truncate max-w-[180px]">{att.filename}</span>
                  {att.size ? (
                    <span className="text-zinc-400 shrink-0">{formatFileSize(att.size)}</span>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
        {text && (
          <p className="whitespace-pre-wrap text-right text-base leading-7 text-zinc-900">
            {text}
          </p>
        )}
      </div>
    </div>
  );
};
